import { Button } from '@/components/ui/button';
import { DockedFooter } from '@/components/DockedFooter';
import { CheckHeader } from './CheckHeader';
import { TaxLine } from './TaxLine';
import { formatMoney } from '@/lib/money';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { useTranslation } from '@/i18n';
import type { Check, Location } from '@/api/types';

interface CheckScreenProps {
    location: Location;
    check: Check;
    onPay?: () => void;
}

/**
 * The check a guest is about to pay.
 *
 * Presentational on purpose — it takes the location and check as props and fetches nothing. The route around
 * it owns loading and errors, which keeps this renderable from fixtures and testable without a network.
 *
 * The page scrolls as one document and the footer is fixed over it, so the amount and the action are always
 * on screen no matter how long the check runs. A guest should never have to scroll to find out what they are
 * about to be charged.
 */
export function CheckScreen({ location, check, onPay }: CheckScreenProps) {
    const { t } = useTranslation();

    useDocumentTitle(t('document.titleAtLocation', { name: location.name }));

    /*
     * Money follows the LOCATION's locale, not the guest's language. These amounts have to match the check
     * printed on the table — a Spanish-speaking guest at a restaurant in Texas should still read "$93.85",
     * the same string that is on the paper in front of them.
     */
    const { currency, locale } = location;
    const money = (minorUnits: number) => formatMoney(minorUnits, currency, locale);

    /*
     * A check somebody has already part-paid. The amount due is what this guest is charged, and the total is
     * only context — showing the total alone would ask them for money that has already been handed over.
     */
    const partPaid = check.amountPaidCents > 0;

    /*
     * Discounts are split by which side of the subtotal they belong on, and that is the only thing decided
     * here — the amounts are the POS's and the subtotal shown is always `subtotalCents`.
     *
     * A pre-subtotal discount has already been taken off that figure, so it is drawn above it, among the
     * things that make it up. A post-subtotal one has not, so it is drawn below. Put a pre-subtotal discount
     * underneath the subtotal and it reads as though it were taken off twice; put a post-subtotal one above
     * and it reads as though it were never taken off at all. Both look like the total is wrong.
     */
    const preSubtotalDiscounts = check.discounts.filter((discount) => discount.isPreSubtotal);
    const postSubtotalDiscounts = check.discounts.filter((discount) => !discount.isPreSubtotal);

    return (
        <div className="min-h-dvh bg-muted">
            <div className="mx-auto w-full max-w-[var(--shell-max-width)]">
                <CheckHeader location={location} check={check} />

                {check.isClosed && (
                    <div className="mx-5 mb-3 rounded-lg border border-border bg-background px-4 py-3 text-sm text-muted-foreground">
                        {t('check.closedNotice')}
                    </div>
                )}

                {/*
                  * Bottom padding clears the fixed footer. Without it the last rows sit permanently underneath
                  * it and cannot be scrolled into view.
                  */}
                <main className="px-5 pb-44">
                    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-xs">
                        <ul className="flex flex-col px-4 py-1" aria-label={t('check.itemsLabel')}>
                            {check.items.map((item) => (
                                <li key={item.id} className="py-2">
                                    <div className="flex items-start justify-between gap-3">
                                        <span className="flex min-w-0 items-start gap-2.5">
                                            {/*
                                              * Shown on every line, matching the receipt, so the column scans.
                                              * The visible glyph is a bare number, which tells a screen reader
                                              * nothing — the sr-only text supplies the word.
                                              *
                                              * That text is a sibling rather than an `aria-label` on the badge
                                              * because ARIA only honours a label on elements whose role
                                              * supports naming. A bare <span> is `generic`, which does not, so
                                              * the label here was free to be dropped entirely — and is, by
                                              * several screen readers.
                                              */}
                                            <span
                                                aria-hidden="true"
                                                className="tabular mt-px shrink-0 rounded bg-accent px-1.5 py-0.5 text-xs font-semibold text-accent-foreground"
                                            >
                                                {item.quantity}
                                            </span>
                                            <span className="sr-only">
                                                {t('check.quantityLabel', { count: item.quantity })}
                                            </span>
                                            {item.name && (
                                                <span className="text-[0.9375rem] leading-snug">{item.name}</span>
                                            )}
                                        </span>
                                        <span className="tabular shrink-0 text-[0.9375rem] text-muted-foreground">
                                            {money(item.totalCents)}
                                        </span>
                                    </div>

                                    {/*
                                      * Modifiers indent to align with the item name, past the quantity badge,
                                      * so they read as belonging to the item above rather than as their own
                                      * line items. Only priced ones show an amount — most cost nothing, and a
                                      * column of "$0.00" would be noise on the one screen that has to make
                                      * charges obvious.
                                      */}
                                    {item.modifiers.length > 0 && (
                                        <ul className="mt-1 flex flex-col gap-0.5 pl-[2.125rem]">
                                            {item.modifiers.map((modifier) => (
                                                <li
                                                    key={modifier.id}
                                                    className="flex items-start justify-between gap-3 text-[0.8125rem] leading-snug text-muted-foreground"
                                                >
                                                    <span className="min-w-0">
                                                        {modifier.quantity !== '1' && `${modifier.quantity} × `}
                                                        {modifier.name}
                                                    </span>
                                                    {modifier.totalCents !== 0 && (
                                                        <span className="tabular shrink-0">
                                                            {money(modifier.totalCents)}
                                                        </span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {check.items.length === 0 && (
                            <p className="px-4 py-10 text-center text-sm text-muted-foreground">
                                {t('check.empty')}
                            </p>
                        )}

                        {/*
                          * Above the rule, because the subtotal below already has these taken off. They sit
                          * with the items for the same reason the items do: together they are what the
                          * subtotal comes to.
                          */}
                        {preSubtotalDiscounts.length > 0 && (
                            <dl className="tabular flex flex-col gap-2 px-4 pb-2 text-sm">
                                {preSubtotalDiscounts.map((discount) => (
                                    <div key={discount.id} className="flex justify-between gap-3">
                                        <dt className="min-w-0 text-muted-foreground">
                                            {discount.name ?? t('check.discount')}
                                        </dt>
                                        <dd className="shrink-0">{money(-discount.amountCents)}</dd>
                                    </div>
                                ))}
                            </dl>
                        )}

                        {/*
                          * The one rule in the card: it separates what you ordered from what it comes to.
                          *
                          * Every figure below is the POS's own. Tax in particular used to be total minus
                          * subtotal, which reported a service charge as tax on any check that carried one.
                          */}
                        <dl className="tabular flex flex-col gap-2 border-t border-border bg-muted/60 px-4 py-3.5 text-sm">
                            <div className="flex justify-between gap-3">
                                <dt className="text-muted-foreground">{t('check.subtotal')}</dt>
                                <dd>{money(check.subtotalCents)}</dd>
                            </div>

                            {/*
                              * Below the rule, because the subtotal above does NOT have these taken off yet.
                              * Named and signed either way: a guest who redeemed a loyalty reward two screens
                              * ago should see it by name, not as a figure that quietly shrank.
                              */}
                            {postSubtotalDiscounts.map((discount) => (
                                <div key={discount.id} className="flex justify-between gap-3">
                                    <dt className="min-w-0 text-muted-foreground">
                                        {discount.name ?? t('check.discount')}
                                    </dt>
                                    <dd className="shrink-0">{money(-discount.amountCents)}</dd>
                                </div>
                            ))}

                            {check.surcharges.map((surcharge) => (
                                <div key={surcharge.id} className="flex justify-between gap-3">
                                    <dt className="min-w-0 text-muted-foreground">
                                        {surcharge.name ?? t('check.surcharge')}
                                    </dt>
                                    <dd className="shrink-0">{money(surcharge.amountCents)}</dd>
                                </div>
                            ))}

                            <TaxLine
                                taxCents={check.taxCents}
                                digitalSurchargeCents={check.digitalSurchargeCents}
                                money={money}
                            />

                            {/*
                              * Only on a check somebody has already part-paid. On every other check the total
                              * and the amount due are the same number, and printing it twice reads as an error.
                              */}
                            {partPaid && (
                                <>
                                    <div className="flex justify-between gap-3 border-t border-border pt-2">
                                        <dt className="text-muted-foreground">{t('check.total')}</dt>
                                        <dd>{money(check.totalCents)}</dd>
                                    </div>
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-muted-foreground">{t('check.alreadyPaid')}</dt>
                                        <dd>{money(-check.amountPaidCents)}</dd>
                                    </div>
                                </>
                            )}
                        </dl>
                    </div>
                </main>
            </div>

            {/* Fixed, so the check scrolls underneath rather than pushing this down the page. */}
            <DockedFooter>
                {/*
                  * The amount due, not the total. They are the same number until somebody part-pays, and on a
                  * check that has been part-paid the total is the wrong figure to put above the Pay button.
                  */}
                <div className="tabular mb-3.5 flex items-baseline justify-between gap-3">
                    <span className="text-[0.9375rem] font-medium text-muted-foreground">
                        {partPaid ? t('check.amountDue') : t('check.total')}
                    </span>
                    <span className="text-[1.75rem] font-semibold leading-none tracking-tight">
                        {money(check.amountDueCents)}
                    </span>
                </div>

                <Button
                    size="lg"
                    className="h-[3.25rem] w-full rounded-xl text-base font-semibold"
                    disabled={check.isClosed}
                    onClick={onPay}
                >
                    {check.isClosed ? t('check.closedAction') : t('check.pay')}
                </Button>
            </DockedFooter>
        </div>
    );
}
